import React, { useCallback } from 'react';

import isBoolean from 'es-toolkit/compat/isBoolean';

import { type FeatureFlagKey, useFeatureFlag, useLaunchDarkly } from '@order/LaunchDarkly';

import { FeatureFlagEditButton, FeatureFlagToggleButton } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const FeatureFlag = (props: Props) => {
  const { flagKey, updateFeatureFlag, editFeatureFlag } = props;

  const { flagOverrides } = useLaunchDarkly();
  const flagActualValue = useFeatureFlag(flagKey);

  // ─────────────────────────────────────────────────────────────────────

  const overrideValue = flagOverrides?.[flagKey];
  const flagValue = overrideValue ?? flagActualValue;
  const isOverwritten = overrideValue !== undefined;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnChange = useCallback(
    (value: boolean) => {
      updateFeatureFlag(flagKey, value);
    },
    [flagKey, updateFeatureFlag],
  );

  const handleOnEdit = useCallback(() => {
    editFeatureFlag(flagKey);
  }, [editFeatureFlag, flagKey]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isBoolean(flagValue)) {
    return (
      <FeatureFlagEditButton name={flagKey} isOverwritten={isOverwritten} onPress={handleOnEdit} />
    );
  }

  return (
    <FeatureFlagToggleButton
      name={flagKey}
      value={flagValue}
      isOverwritten={isOverwritten}
      onChange={handleOnChange}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  flagKey: FeatureFlagKey;
  updateFeatureFlag: (flagName: FeatureFlagKey, flagValue: boolean) => void;
  editFeatureFlag: (flagName: FeatureFlagKey) => void;
};
